import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import '../../../vendor/svg.pathmorphing.js';
import { AppConfigService } from '@core/appconfig.service';
import { AvatarService } from '@core/avatar.service';
import { BrowserService } from '@core/browser.service';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { Avatar, AvatarStarRequest, AvatarSticker } from '@models/avatar';
import { Quest } from '@models/quest';
import { QuestCollection } from '@models/quest-collection';
import { UpdateQuestRequest } from '@models/quest-request';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { QuestService } from '@services/quest/quest.service';
import { AvatarStarEarnType } from '@shared/enums/avatar-type';
import { GradeType } from '@shared/enums/grade-type';
import { Element, G, Gradient, Runner, SVG, Timeline } from '@svgdotjs/svg.js';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { from, interval, of, zip, BehaviorSubject, Observable, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ProductType } from '../enums/product-type';
import { QuestType } from '../enums/quest-type';
import { StudentAvatarModalComponent } from '../student-avatar-modal/student-avatar-modal.component';
import { StudentAvatarStoryModalComponent } from '../student-avatar-story-modal/student-avatar-story-modal.component';

import {
  blink,
  blinkLeftRight,
  flapArms,
  spinEyebrows,
  spinEyes,
  whimsicalBloat,
  whimsicalVanish,
  wiggle,
  BlobAnimation,
} from './animations';

@Component({
  selector: 'zbp-student-avatar',
  templateUrl: './student-avatar.component.html',
  styleUrls: ['./student-avatar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [BrowserService, QuestService],
  standalone: false
})
export class StudentAvatarComponent implements OnDestroy, OnInit {
  @Input() collectionsSubject: Observable<QuestCollection[]>;
  @Input() productType: ProductType = null;
  @Input() gradeType: GradeType | string = null;
  @Output() earnedSticker: EventEmitter<AvatarSticker[]> = new EventEmitter<AvatarSticker[]>();
  @ViewChild('blobContainer', { static: true }) blobContainer: ElementRef<any>;

  private blobEl: Element;
  private idleTimeline: any;
  private idleRunner: Runner;
  private idleIndex: number = 0;
  private teaseTimeline: any;
  private teaseRunner: Runner;
  private teaseEarnStar = false;
  private teaseQuest: Quest;
  private teaseQuestComplete = true;
  private triedTeaseCount = 0;
  private _modalRef: NgbModalRef;
  private avatarEventSubscription: Subscription;
  private avatarSubscription: Subscription;
  private questsSubscription: Subscription;

  animationPlaying = false;
  hasAudioSupport = false;

  idleAnimations: BlobAnimation[] = [
    blink,
    flapArms,
    blinkLeftRight,
  ];

  svgAnimations: BlobAnimation[] = [
    spinEyes,
    spinEyebrows,
    whimsicalBloat,
    whimsicalVanish,
    wiggle,
  ];

  avatar: Avatar = null;
  loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  starsEarned: number = 0;

  constructor(
    private avatarService: AvatarService,
    private userService: UserService,
    private appConfig: AppConfigService,
    private browserService: BrowserService,
    private modalService: NgbModal,
    private questService: QuestService,
    private toastr: ToastrService) { }

  ngOnDestroy() {
    if (this.blobContainer) {
      this.disassembleBlob();
    }
    if (this.avatarSubscription) {
      this.avatarSubscription.unsubscribe();
    }
    if (this.avatarEventSubscription) {
      this.avatarEventSubscription.unsubscribe();
    }
    if (this.questsSubscription) {
      this.questsSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.userService.hasAudioSupport$
      .subscribe((value) => {
        this.hasAudioSupport = value;
      });

    this.avatarEventSubscription = this.avatarService.avatarEvent$
      .subscribe(() => {
        this.loadAvatar();
      });

    // Subscribe to Quest changes
    this.questsSubscription = this.collectionsSubject
      .pipe(
        map((collections: QuestCollection[]) => (collections
          ? collections.reduce((quests: Quest[], c: QuestCollection) => quests.concat(c.quests), [])
            .find(q => q.questKey === QuestType.AvatarTease && !q.isComplete)
          : [])),
      )
      .subscribe((quest: Quest) => {
        this.teaseQuest = quest;
        this.teaseQuestComplete = quest ? quest.isComplete : true;
      }, (err) => {
        console.error(err);
        this.teaseQuest = undefined;
      });
  }

  get blob(): Element {
    return this.blobEl;
  }

  loadAvatar(): void {
    this.avatarSubscription = this.initializeBlob()
      .pipe(
        switchMap((avatar: Avatar) => {
          if (!this.avatar) {
            // Sets the avatar initially.
            this.avatar = _.cloneDeep<Avatar>(avatar);
          } else if (avatar && avatar.name) {
            // Merges current avatar with updated avatar if the returned avatar is valid.
            _.merge(this.avatar, avatar);
          } else {
            // The updated avatar is bad so don't need to re-assemble the blob.
            throw new Error('An error occurred retrieving an update your blob. Please refresh the page to continue.');
          }
          this.checkForEarnedSticker();
          return this.assembleBlob(this.avatar.sticker);
        }),
        catchError((err) => {
          console.error(err);
          this.toastr.error(err);
          return of(null);
        })
      )
      .subscribe(() => {
        // Reset animation playing if the blob was replaced.
        this.animationPlaying = false;
        this.loaded.next(true);
      });
  }

  editAvatar(): void {
    this._modalRef = this.modalService.open(StudentAvatarModalComponent, { backdrop: 'static', size: 'md' });
    this._modalRef.componentInstance.avatar = this.avatar;
    this._modalRef.componentInstance.productType = this.productType;
    this._modalRef.componentInstance.gradeType = this.gradeType;

    from(this._modalRef.result)
      .subscribe((avatar: Avatar) => {
        this.avatar = avatar;
        this.changeColor(avatar.color);
      }, (errors: string[]) => {
        if (errors.length > 0) {
          const errorItems = errors.map(err => `${err}`);
          const errorList = `${errorItems}`;
          this.toastr.error(errorList);
        }
      });
  }

  private initializeBlob(): Observable<Avatar> {
    return this.avatarService.getAvatar(this.productType, this.gradeType)
      .pipe(
        mergeMap((res: ApiResponse<Avatar>) => {
          if (res.success) {
            this.avatar = res.response;
            if (!this.starsEarned) {
              this.starsEarned = this.avatar && this.avatar.level ? this.avatar.level.totalStarCount : 0;
              this.checkForEarnedSticker();
            }
          } else {
            this.avatar = {
              name: '',
              color: null,
              level: { name: '', levelNumber: 1, totalStarCount: 0, percentageComplete: 0 },
              sticker: null,
            };
          }

          return of(this.avatar);
        }),
        mergeMap(() => {
          if (!this.avatar.name) {
            this._modalRef = this.modalService.open(StudentAvatarStoryModalComponent, {
              windowClass: 'full-screen-modal',
              backdrop: 'static',
            });
            this._modalRef.componentInstance.productType = this.productType;
            this._modalRef.componentInstance.gradeType = this.gradeType;
            return from(this._modalRef.result);
          }
          return of(this.avatar);
        })
      );
  }

  assembleBlob(sticker?: AvatarSticker): Observable<Element> {
    const blobParts = ['assets/img/blob.svg'];

    // Destroy any current blob.
    this.disassembleBlob();

    if (sticker) {
      blobParts.push(`${this.appConfig.avatarUrl}/stickers/${sticker.mediaName}.svg`);
    }

    const observables = blobParts.map(url => this.avatarService.getAvatarPart(url));
    return zip(...observables)
      .pipe(
        map((parts: SVGElement[]) => {
          const imported: Element = SVG<SVGElement>(parts[0]);
          if (imported) {
            const el = imported.addTo(this.blobContainer.nativeElement);

            if (parts.length === 2 && parts[1]) {
              // Attaches the sticker to the blob.
              return this.attachSticker(parts[1], el);
            }

            return el;
          }

          // Display a friendlier error message rather than the TypeError.
          throw new Error('Avatar could not be loaded');
        }),
        mergeMap((svgEl: Element) => {
          this.blobEl = svgEl;
          this.changeColor(this.avatar.color);
          this.setupIdle();

          return of(this.blobEl);
        })
      );
  }

  playIdle(): void {
    if (this.animationPlaying || this.idleAnimations.length === 0) {
      return;
    }

    // Start the animation at the current index.
    this.animationPlaying = true;
    this.idleAnimations[this.idleIndex](this.blobEl, this.idleTimeline);

    // Schedule the runner on the timeline, which starts the animation.
    this.idleTimeline.schedule(this.idleRunner);
  }

  tease(): void {
    if (this.animationPlaying) {
      return;
    }

    const avatarStarRequest: AvatarStarRequest = {
      earnReason: AvatarStarEarnType.AvatarTease,
      gradeType: this.gradeType,
    };
    // Complete AvatarTease Quest if it exists
    if (this.teaseQuest && !this.teaseQuestComplete) {
      this.teaseQuestComplete = true;
      const request = [{
        questId: this.teaseQuest.questId,
        questKey: this.teaseQuest.questKey,
        classroomId: this.teaseQuest.classroomId,
        grade: this.gradeType,
        isComplete: true,
        productTypes: [ProductType.None]
      }] as UpdateQuestRequest[];

      this.questService.updateQuests(request)
        .pipe(
          mergeMap((res) => {
            if (!res.success) {
              console.error(res.messages);
              this.toastr.error(res.messages.join('\n'));
            }
            return this.avatarService.earnStar(avatarStarRequest);
          })
        )
        .subscribe((res) => {
          if (res.success) {
            this.teaseEarnStar = true;
          } else {
            console.error(res.messages);
            this.toastr.error(res.messages.join('\n'));
          }
        }, (err: any) => {
          console.error(err);
          this.toastr.error(err);
        });
    } else if (!this.teaseEarnStar) {
      // Attempt to earn star - Star can only be earned once per student
      this.avatarService.earnStar(avatarStarRequest)
        .subscribe((res) => {
          if (res.success) {
            this.teaseEarnStar = true;
          } else {
            console.error(res.messages);
            this.toastr.error(res.messages.join('\n'));
          }
        }, (err: any) => {
          console.error(err);
          this.toastr.error(err);
        });
    }

    this.teaseTimeline = new Timeline() as any;

    // Triggers a random animation.
    const animations = _.shuffle(this.svgAnimations);
    this.animationPlaying = true;
    if (animations.length > 0) {
      const runnerTime = animations[0](this.blobEl, this.teaseTimeline);

      if (!runnerTime) {
        // This gives a chance for a dynamic animation based on a sticker to not fire for some reason.
        if (this.triedTeaseCount < 3) {
          this.triedTeaseCount++;
          this.animationPlaying = false;
          this.tease();
          return;
        }
        this.triedTeaseCount = 0;
        return;
      }

      this.teaseRunner = new Runner(runnerTime) as any;
      this.teaseRunner.after(() => {
        this.animationPlaying = false;
      });

      this.triedTeaseCount = 0;
      this.teaseTimeline.schedule(this.teaseRunner);
    }
  }

  private setupIdle(): void {
    if (this.idleAnimations.length > 0) {
      // The type definitions for svg.js are bad so in order to use functionality that exists in the native library
      // the types for these variables are casted to "any". This makes TypeScript not error out on build time.
      this.idleRunner = new Runner(2500) as any;
      this.idleTimeline = new Timeline() as any;

      this.idleRunner.after(() => {
        this.idleIndex = this.idleIndex + 1 === this.idleAnimations.length ? 0 : this.idleIndex + 1;
        this.animationPlaying = false;
      });

      interval(10000)
        .subscribe(() => {
          this.playIdle();
        }, (err) => {
          console.error(err);
        });
    }
  }

  private changeColor(colorValue: string): void {
    const color = colorValue !== null ? colorValue : '#FFB057';
    const bottomColor = this.getColorChange(color);
    const strokeColor = this.getStrokeChange(color);

    const gradient1 = this.blobEl.findOne('#bodyGradient1') as Gradient;
    gradient1.update((gradient: Gradient) => {
      gradient.stop({ offset: 0, color });
      gradient.stop({ offset: 1, color: bottomColor });
    });
    const gradient2 = this.blobEl.findOne('#bodyGradient2') as Gradient;
    gradient2.update((gradient: Gradient) => {
      this.updateBodyArmGradient(gradient, color, bottomColor);
    });
    const gradient3 = this.blobEl.findOne('#leftArmGradient') as Gradient;
    gradient3.update((gradient: Gradient) => {
      this.updateBodyArmGradient(gradient, color, bottomColor);
    });
    const gradient4 = this.blobEl.findOne('#rightArmGradient') as Gradient;
    gradient4.update((gradient: Gradient) => {
      this.updateBodyArmGradient(gradient, color, bottomColor);
    });
    this.blobEl.findOne('#bodyOutline1').attr('stroke', strokeColor);
    this.blobEl.findOne('#bodyOutline2').attr('stroke', strokeColor);
    this.blobEl.findOne('#leftArm').attr('stroke', strokeColor);
    this.blobEl.findOne('#rightArm').attr('stroke', strokeColor);

    if (this.browserService.isSafari) {
      // Safari does not do gradients correctly.
      const { location } = window;
      const urlPrefix = `${location.protocol}//${location.host}${location.pathname}`;
      const blobStyle = this.blobEl.root().defs().findOne('style') as any;
      blobStyle
        .rule('#bodyOutline1', { fill: `url(${urlPrefix}#bodyGradient1) ${color}` })
        .rule('#bodyOutline2', { fill: `url(${urlPrefix}#bodyGradient2) ${color}` })
        .rule('#leftArm', { fill: `url(${urlPrefix}#leftArmGradient) ${color}` })
        .rule('#rightArm', { fill: `url(${urlPrefix}#rightArmGradient) ${color}` });
    }
  }

  private updateBodyArmGradient(gradient: Gradient, color: string, bottomColor: string): void {
    gradient.stop({ offset: 0, color });
    gradient.stop({ offset: 0.45, color });
    gradient.stop({ offset: 0.4746, color });
    gradient.stop({ offset: 1, color: bottomColor });
  }

  private getColorChange(color: string): string {
    const colorParts = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i).slice(1);
    return colorParts
      .map((hexValue: string) => this.darken(hexValue, .65))
      .reduce((result: string, value: string) => `${result}${value}`, '#');
  }

  private getStrokeChange(color: string): string {
    const colorParts = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i).slice(1);
    return colorParts
      .map((hexValue: string) => this.darken(hexValue, .4))
      .reduce((result: string, value: string) => `${result}${value}`, '#');
  }

  private darken(hexValue: string, amount: number): string {
    const value = parseInt(hexValue, 16) * amount;
    const hex = Math.round(value).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }

  private getStarsEarned(stars?: any): number {
    if (stars && stars.starCount) {
      return stars.starCount;
    }

    return this.avatar && this.avatar.level ? this.avatar.level.totalStarCount : 0;
  }

  private checkForEarnedSticker(): void {
    if (this.avatar && this.avatar.availableStickersToEarn && this.avatar.availableStickersToEarn.length > 0) {
      this.earnedSticker.emit(this.avatar.availableStickersToEarn);
    }
  }

  private attachSticker(svg: SVGElement, parent: Element): Element {
    const stickerSvg: Element = SVG<SVGElement>(svg);
    const stickerGraphic = stickerSvg.findOne('.sticker') as G;
    const stickerGroup = parent.findOne('#sticker') as G;
    const stickerDefinitions = stickerSvg.root().defs();
    const attachX = stickerGraphic.attr('data-attach-x');
    const attachY = stickerGraphic.attr('data-attach-y');

    if (stickerDefinitions) {
      // Imports each child of defs element if it exists on the sticker.
      stickerDefinitions.children().each((child: Element) => {
        const href = child.attr('xlink:href');
        const { location } = window;
        const urlPrefix = `${location.protocol}//${location.host}${location.pathname}`;
        if (href) {
          child.attr('href', `${urlPrefix}${href}`);
        }

        if (attachX && attachY) {
          const attr = child.attr();
          if (child.type === 'linearGradient') {
            // Adjusts the gradient from/to position by the attach coordinates.
            attr.x1 += attachX;
            attr.y1 += attachY;
            attr.x2 += attachX;
            attr.y2 += attachY;
            child.attr(attr);
          } else if (child.type === 'radialGradient') {
            // Adjusts the center of the radial gradient by the attach coordinates.
            attr.cx += attachX;
            attr.cy -= attachY;

            // Adjusts attributes that are optional.
            if (attr.fx) {
              attr.fx += attachX;
            }
            if (attr.fy) {
              attr.fy -= attachY;
            }
            child.attr(attr);
          }
        }

        parent.root().defs().add(child);
      });
    }

    stickerGraphic.addTo(stickerGroup);

    if (attachX && attachY) {
      // Moves the sticker to the correct position on the blob based on its configuration.
      stickerGraphic.move(attachX, attachY);
    }

    return parent;
  }

  private disassembleBlob(): void {
    // Avoids jquery for speed.
    if (this.blobContainer.nativeElement
      && this.blobContainer.nativeElement.children
      && this.blobContainer.nativeElement.children.length > 0) {
      for (let i = 0; i < this.blobContainer.nativeElement.children.length; i++) {
        this.blobContainer.nativeElement.removeChild(this.blobContainer.nativeElement.children[i]);
      }
    }
  }
}
